import React, { useState } from "react";
import { styled, Typography, Grid, Divider, IconButton } from "@mui/material";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { v4 as uuidv4 } from "uuid";

import LinkButton from "../LinkButton";
import PrimaryButton from "../PrimaryButton";
import SecondaryButton from "../SecondaryButton";
import CustomIcon from "../CustomIcon";
import minusCircleIcon from "../../../assets/icons/minusCircle.svg";
import plusCircleIcon from "../../../assets/icons/plusCircle.svg";
import dndIcon from "../../../assets/icons/dnd.svg";

const CustomizationContainer = styled(Grid)(({ theme }) => ({
  width: 600,
  height: 370,
  maxHeight: 370,
  padding: "1rem",
}));

const Footer = styled("div")(({ theme }) => ({
  width: "100%",
  height: 70,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  backgroundColor: "var(--gray-6)",
  borderTop: "1px solid var(--gray-5)",
  paddingRight: "1rem",
}));

const FooterActions = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  gap: 2,
}));

const reorderColumnList = (sourceCol, startIndex, endIndex) => {
  const newItems = Array.from(sourceCol.items);
  const [removed] = newItems.splice(startIndex, 1);
  newItems.splice(endIndex, 0, removed);

  const newColumn = {
    ...sourceCol,
    items: newItems,
  };

  return newColumn;
};

const Column = ({ data, handleItemClick }) => {
  return (
    <Grid
      container
      direction="column"
      spacing={2}
      sx={{ paddingBottom: "1rem" }}
    >
      <Grid item xs={12}>
        <Typography sx={{ color: "var(--gray-1)", paddingBottom: "0.5rem" }}>
          {data.name}
        </Typography>
      </Grid>
      <Droppable droppableId={data.id}>
        {(provided, snapshot) => (
          <Grid
            sx={{ minHeight: "50px" }}
            item
            container
            direction="column"
            spacing={2}
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            {data.items.map((item, index) => {
              return (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {(provided, snapshot) => {
                    return (
                      <Grid
                        item
                        xs={12}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        ref={provided.innerRef}
                      >
                        <Grid
                          container
                          alignItems="center"
                          sx={{
                            height: 41,
                            border: "1px solid var(--gray-4)",
                            borderRadius: 2,
                            backgroundColor: "var(--gray-6)",
                            boxShadow: snapshot.isDragging
                              ? "0 0 4px 0 rgba(0,0,0,0.13)"
                              : "unset",
                          }}
                        >
                          <Grid item xs={1.5}>
                            <CustomIcon svgIcon={dndIcon} fontSize="small" />
                          </Grid>
                          <Grid item xs={9}>
                            <Typography
                              style={{
                                color: "var(--secondary)",
                                fontSize: 14,
                                letterSpacing: "-0.47px",
                                lineHeight: "16px",
                                textTransform: "capitalize",
                              }}
                            >
                              {item?.content}
                            </Typography>
                          </Grid>
                          <Grid item xs={1.5}>
                            <IconButton
                              size="small"
                              onClick={() => handleItemClick(data.id, item)}
                            >
                              <CustomIcon
                                svgIcon={
                                  data.id === "allColumns"
                                    ? plusCircleIcon
                                    : minusCircleIcon
                                }
                                imgStyles={{ width: 22 }}
                              />
                            </IconButton>
                          </Grid>
                        </Grid>
                        {provided.placeholder}
                      </Grid>
                    );
                  }}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </Grid>
        )}
      </Droppable>
    </Grid>
  );
};

const Board = ({ columns, onDragEnd, handleItemClick, reportHeader }) => {
  const { inUseColumns, allColumns } = columns;
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Grid container justifyContent="flex-start">
        <Grid item xs={5.5}>
          <Column
            data={{ ...inUseColumns }}
            handleItemClick={handleItemClick}
          />
        </Grid>
        <Grid item xs={1} container justifyContent="center">
          <Divider orientation="vertical" />
        </Grid>
        <Grid item xs={5.5}>
          <Column data={{ ...allColumns }} handleItemClick={handleItemClick} />
        </Grid>
      </Grid>
    </DragDropContext>
  );
};

const getColumns = (inUseItems, allItems, reportHeader) => {
  return {
    inUseColumns: {
      id: "inUseColumns",
      name: "In Use Columns",
      items: [
        ...inUseItems.map((column) => ({
          ...column,
          content: column[reportHeader ? "title" : "headerName"],
          id: uuidv4(),
        })),
      ],
    },
    allColumns: {
      id: "allColumns",
      name: "All Columns",
      items: [
        ...allItems
          .filter(
            (column) =>
              !inUseItems.some(
                (inUseColumn) =>
                  inUseColumn[reportHeader ? "key" : "field"] ===
                  column[reportHeader ? "key" : "field"]
              )
          )
          .map((column) => ({
            ...column,
            content: column[reportHeader ? "title" : "headerName"],
            id: uuidv4(),
          })),
      ],
    },
  };
};

const TableCustomization = ({
  reportHeader = false,
  defaultItems,
  allItems,
  inUseItems,
  handleSaveCustomization,
  handleCancelCustomization,
  setColumnsOnChange,
}) => {
  const initialColumns = {
    ...getColumns(inUseItems, allItems, reportHeader),
  };

  const [columns, setColumns] = useState(initialColumns);

  const onDragEnd = (result) => {
    const { destination, source } = result;

    // If user tries to drop in an unknown destination
    if (!destination) return;

    // if the user drags and drops back in the same position
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // If the user drops within the same column but in a different positoin
    const sourceCol = columns[source.droppableId];
    const destinationCol = columns[destination.droppableId];

    if (sourceCol.id === destinationCol.id) {
      const newColumn = reorderColumnList(
        sourceCol,
        source.index,
        destination.index
      );

      const updatedColumns = { ...columns, [newColumn.id]: newColumn };

      setColumns({ ...updatedColumns });
      if (setColumnsOnChange)
        setColumnsOnChange(updatedColumns.inUseColumns.items);

      return;
    }

    // If the user moves from one column to another
    const startItems = Array.from(sourceCol.items);
    const [removed] = startItems.splice(source.index, 1);
    const newStartCol = {
      ...sourceCol,
      items: startItems,
    };

    const endItems = Array.from(destinationCol.items);
    endItems.splice(destination.index, 0, removed);
    const newEndCol = {
      ...destinationCol,
      items: endItems,
    };

    const updatedColumns = {
      ...columns,
      [newStartCol.id]: newStartCol,
      [newEndCol.id]: newEndCol,
    };
    setColumns({ ...updatedColumns });
    if (setColumnsOnChange)
      setColumnsOnChange(updatedColumns.inUseColumns.items);
  };

  const handleItemClick = (columnId, item) => {
    const { allColumns, inUseColumns } = columns;

    const updatedAllColumns = {
      ...allColumns,
      items:
        columnId === "allColumns"
          ? allColumns.items.filter((curItem) => curItem.id !== item.id)
          : [...allColumns.items, { ...item }],
    };

    const updatedInUseColumns = {
      ...inUseColumns,
      items:
        columnId === "allColumns"
          ? [...inUseColumns.items, { ...item }]
          : inUseColumns.items.filter((curItem) => curItem.id !== item.id),
    };

    const newColumns = {
      ...columns,
      allColumns: updatedAllColumns,
      inUseColumns: updatedInUseColumns,
    };

    setColumns(newColumns);
    if (setColumnsOnChange) setColumnsOnChange(newColumns.inUseColumns.items);
  };

  const handleReset = () => {
    const updatedColumns = {
      ...getColumns(defaultItems, allItems, reportHeader),
    };
    setColumns({ ...updatedColumns });
    if (setColumnsOnChange)
      setColumnsOnChange(updatedColumns.inUseColumns.items);
  };

  const handleCancel = () => {
    handleCancelCustomization();
  };

  const handleSave = () => {
    handleSaveCustomization(columns.inUseColumns.items);
  };

  return (
    <div>
      <CustomizationContainer container>
        <Grid item xs={12} sx={{ height: "10%" }}>
          <Typography variant="h6" sx={{ color: "var(--secondary)" }}>
            Customize Columns
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          container
          sx={{
            height: "90%",
            maxHeight: "90%",
            overflow: "hidden",
            overflowY: "auto",
            paddingTop: "1rem",
            paddingRight: "0.5rem",
          }}
        >
          <Board
            reportHeader={reportHeader}
            columns={columns}
            onDragEnd={onDragEnd}
            handleItemClick={handleItemClick}
          />
        </Grid>
      </CustomizationContainer>
      <Grid container>
        <Footer>
          <LinkButton sx={{ color: "var(--secondary)" }} onClick={handleReset}>
            Reset
          </LinkButton>
          <FooterActions>
            <PrimaryButton onClick={handleCancel}>Cancel</PrimaryButton>
            <SecondaryButton onClick={handleSave}>Save</SecondaryButton>
          </FooterActions>
        </Footer>
      </Grid>
    </div>
  );
};

export default TableCustomization;
